import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import {
    Menu,
    LayoutDashboard,
    ClipboardList,
    FileText,
    FileSignature,
    List,
    HandCoins,
    LogOut,
} from 'lucide-react';

const ListItem = React.forwardRef<
    React.ElementRef<'button'>,
    React.ComponentPropsWithoutRef<'button'> & { title: string; icon?: React.ReactNode }
>(({ className, title, children, icon, ...props }, ref) => {
    return (
        <li>
            <NavigationMenuLink asChild>
                <button
                    ref={ref}
                    className={cn(
                        'block w-full text-left select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
                        className
                    )}
                    {...props}
                >
                    <div className="flex items-center gap-2 text-sm font-medium leading-none">
                        {icon}
                        {title}
                    </div>
                    {children && (
                        <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1">
                            {children}
                        </p>
                    )}
                </button>
            </NavigationMenuLink>
        </li>
    );
});
ListItem.displayName = 'ListItem';

export interface SectionItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    description?: string;
}

export interface SectionGroup {
    title: string;
    items: SectionItem[];
}

const SECTION_GROUPS: SectionGroup[] = [
    {
        title: 'Overview',
        items: [
            { id: 'overview/summary', label: 'Summary', icon: <LayoutDashboard className="h-4 w-4" />, description: 'Dashboard and portfolio stats' },
        ],
    },
    {
        title: 'Fleet',
        items: [
            { id: 'fleet/procurement', label: 'Procurement', icon: <ClipboardList className="h-4 w-4" />, description: 'Procurement pipeline' },
            { id: 'fleet/contracts', label: 'Contracts', icon: <FileText className="h-4 w-4" />, description: 'Contract lifecycle stages' },
            // THE SCHEDULE IS ABOUT A VEHICLE, so it sits with the fleet rather than under a
            // heading of its own. The id is flat, and the layout routes a section id straight
            // to a path — so this item is `/agreements`, which is where the ceremony and the
            // at-rest reader both send a partner back to.
            { id: 'agreements', label: 'Documents', icon: <FileSignature className="h-4 w-4" />, description: 'Vehicle schedules' },
        ],
    },
    {
        title: 'Finance',
        items: [
            { id: 'finance/earnings', label: 'Earnings', icon: <List className="h-4 w-4" />, description: 'Detailed breakdown by period' },
            { id: 'finance/payouts', label: 'Payouts', icon: <HandCoins className="h-4 w-4" />, description: 'Payout history and details' },
        ],
    },
];

interface SectionDrawerProps {
    activeSection: string;
    onSectionChange: (section: string) => void;
    onSignOut?: () => void;
}

// Desktop NavigationMenu
export function DesktopNav({ activeSection, onSectionChange }: { activeSection: string; onSectionChange: (id: string) => void }) {
    const isGroupActive = (group: SectionGroup) =>
        group.items.some(item => item.id === activeSection);

    return (
        <NavigationMenu>
            <NavigationMenuList>
                {SECTION_GROUPS.map((group) => (
                    <NavigationMenuItem key={group.title}>
                        <NavigationMenuTrigger
                            className={cn(isGroupActive(group) && 'bg-accent')}
                        >
                            {group.title}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                            <ul className="grid w-[300px] gap-2 p-3">
                                {group.items.map((item) => (
                                    <ListItem
                                        key={item.id}
                                        title={item.label}
                                        icon={item.icon}
                                        onClick={() => onSectionChange(item.id)}
                                        className={cn(
                                            activeSection === item.id && 'bg-accent'
                                        )}
                                    >
                                        {item.description}
                                    </ListItem>
                                ))}
                            </ul>
                        </NavigationMenuContent>
                    </NavigationMenuItem>
                ))}
            </NavigationMenuList>
        </NavigationMenu>
    );
}

// Mobile drawer (hamburger menu)
export default function SectionDrawer({ activeSection, onSectionChange, onSignOut }: SectionDrawerProps) {
    const [open, setOpen] = useState(false);

    const handleSelect = (sectionId: string) => {
        onSectionChange(sectionId);
        setOpen(false);
    };

    const activeSectionItem = SECTION_GROUPS.flatMap(g => g.items).find(i => i.id === activeSection);

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2 text-sm font-medium"
                >
                    <Menu className="h-5 w-5" />
                    <span className="truncate max-w-[200px]">
                        {activeSectionItem?.label || 'Summary'}
                    </span>
                </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] px-0 flex flex-col">
                <SheetHeader className="sr-only">
                    <SheetTitle>Navigation</SheetTitle>
                </SheetHeader>
                <div className="overflow-y-auto flex-1">
                    {SECTION_GROUPS.map((group, groupIdx) => (
                        <div key={group.title}>
                            {groupIdx > 0 && <Separator className="my-2" />}
                            <div className="px-4 py-2">
                                <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                                    {group.title}
                                </span>
                            </div>
                            <nav className="flex flex-col gap-0.5 px-2">
                                {group.items.map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => handleSelect(item.id)}
                                        className={cn(
                                            'flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors',
                                            'hover:bg-accent hover:text-accent-foreground',
                                            'active:scale-[0.98]',
                                            activeSection === item.id
                                                ? 'bg-primary/10 text-primary'
                                                : 'text-foreground/70'
                                        )}
                                    >
                                        <span className={cn(
                                            activeSection === item.id ? 'text-primary' : 'text-muted-foreground'
                                        )}>
                                            {item.icon}
                                        </span>
                                        {item.label}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    ))}
                </div>

                {onSignOut && (
                    <div className="mt-auto border-t px-2 py-4">
                        <button
                            onClick={onSignOut}
                            className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 active:scale-[0.98]"
                        >
                            <LogOut className="h-5 w-5" />
                            Sign Out
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

export { SECTION_GROUPS };
